import type { Tone } from "@/lib/tone";

const DOT_CLASS: Record<Tone, string> = {
  critical: "bg-ks-fail",
  watch: "bg-ks-warn",
  ok: "bg-ks-pass",
  neutral: "bg-ks-grey-2",
};

/**
 * The producer-run freshness stamp (W9, parity polish): `generated_at` as a
 * short date plus its age in whole days, with a dot toned by how stale that
 * age is. Shared across both modules' headers so "when did this last run"
 * reads identically everywhere it appears.
 */
export function UpdatedStamp({ generatedAt }: { generatedAt: string | null }) {
  // A truthy-but-unparseable `generated_at` ("not-a-date") is a DIFFERENT
  // fact from a genuinely absent one - both must render "unknown", never a
  // string built from a date that failed to parse.
  const parsed = generatedAt ? new Date(generatedAt) : null;
  const valid = parsed !== null && !Number.isNaN(parsed.getTime());
  const days = valid ? Math.floor((Date.now() - parsed.getTime()) / 86_400_000) : null;
  const tone = ageTone(days);

  return (
    <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
      <span aria-hidden className={`h-1.5 w-1.5 rounded-full ${DOT_CLASS[tone]}`} />
      {valid ? `Updated ${formatDate(parsed)}${days !== null ? `, ${days}d ago` : ""}` : "Updated - unknown"}
    </span>
  );
}

// pass <= 3d, warn <= 7d, fail beyond - the same bands the region freshness
// strips already use, so a reader does not learn a second threshold.
function ageTone(days: number | null): Tone {
  if (days === null) return "neutral";
  if (days <= 3) return "ok";
  if (days <= 7) return "watch";
  return "critical";
}

function formatDate(parsed: Date): string {
  return new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short" }).format(parsed);
}
